import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Download,
  Search,
  Calendar,
  Users,
  Mail,
  Phone,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileSpreadsheet
} from 'lucide-react';
import toast from 'react-hot-toast';
import landingPageService from '../../services/landingPageService';
import { FormLead, LandingPage } from '../../types/pageBuilder';

interface LeadsModalProps {
  isOpen: boolean;
  onClose: () => void;
  landingPageId: string;
  landingPageName: string;
}

const LeadsModal: React.FC<LeadsModalProps> = ({
  isOpen,
  onClose,
  landingPageId,
  landingPageName
}) => {
  const { t } = useTranslation();

  const [leads, setLeads] = useState<FormLead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());

  // Load leads
  const loadLeads = useCallback(async () => {
    if (!landingPageId) return;

    setIsLoading(true);
    try {
      const response = await landingPageService.getLeads(landingPageId, {
        page: currentPage,
        limit: 10,
        startDate: startDate || undefined,
        endDate: endDate || undefined
      });

      if (response.success && response.data) {
        setLeads(response.data);
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalLeads(response.pagination.total);
        }
      }
    } catch (error) {
      console.error('Error loading leads:', error);
      toast.error('Error loading leads');
    } finally {
      setIsLoading(false);
    }
  }, [landingPageId, currentPage, startDate, endDate]);

  useEffect(() => {
    if (isOpen) {
      loadLeads();
    }
  }, [isOpen, loadLeads]);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setSearchTerm('');
      setStartDate('');
      setEndDate('');
      setSelectedLeads(new Set());
    }
  }, [isOpen, landingPageId]);

  // Filter leads by search term (must be before any early return to keep hooks stable)
  const filteredLeads = React.useMemo(() => {
    return leads.filter(lead => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        (lead.email?.toLowerCase().includes(searchLower)) ||
        (lead.name?.toLowerCase().includes(searchLower)) ||
        (lead.phone?.includes(searchTerm)) ||
        (JSON.stringify(lead.form_data || {}).toLowerCase().includes(searchLower))
      );
    });
  }, [leads, searchTerm]);

  // Get all unique custom fields from form_data (excluding standard fields)
  const standardFields = ['email', 'name', 'nome', 'phone', 'telefone', 'tel'];
  const customFields = React.useMemo(() => {
    const fieldsSet = new Set<string>();
    filteredLeads.forEach(lead => {
      if (lead.form_data) {
        Object.keys(lead.form_data).forEach(key => {
          if (!standardFields.includes(key.toLowerCase())) {
            fieldsSet.add(key);
          }
        });
      }
    });
    return Array.from(fieldsSet);
  }, [filteredLeads]);

  if (!isOpen) return null;

  // Toggle lead selection
  const handleSelectLead = (leadId: string) => {
    const newSelected = new Set(selectedLeads);
    if (newSelected.has(leadId)) {
      newSelected.delete(leadId);
    } else {
      newSelected.add(leadId);
    }
    setSelectedLeads(newSelected);
  };

  // Select all
  const handleSelectAll = () => {
    if (selectedLeads.size === filteredLeads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(filteredLeads.map(l => l.id)));
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const leadsToExport = selectedLeads.size > 0
      ? filteredLeads.filter(l => selectedLeads.has(l.id))
      : filteredLeads;

    if (leadsToExport.length === 0) {
      toast.error('No leads to export');
      return;
    }

    // Get all unique form_data keys
    const allKeys = new Set<string>();
    leadsToExport.forEach(lead => {
      if (lead.form_data) {
        Object.keys(lead.form_data).forEach(key => allKeys.add(key));
      }
    });

    const baseHeaders = ['Email', 'Nome', 'Telefone', 'Data', 'UTM Source', 'UTM Medium', 'UTM Campaign'];
    const formDataHeaders = Array.from(allKeys).filter(k => !['email', 'name', 'nome', 'phone', 'telefone'].includes(k.toLowerCase()));
    const headers = [...baseHeaders, ...formDataHeaders];

    const rows = leadsToExport.map(lead => {
      const formData = lead.form_data || {};
      const baseData = [
        lead.email || formData.email || '',
        lead.name || formData.name || formData.nome || '',
        lead.phone || formData.phone || formData.telefone || '',
        formatDate(lead.created_at || lead.createdAt || ''),
        lead.utm_source || '',
        lead.utm_medium || '',
        lead.utm_campaign || ''
      ];

      const formDataValues = formDataHeaders.map(key => {
        const value = formData[key];
        return typeof value === 'object' ? JSON.stringify(value) : (value || '');
      });

      return [...baseData, ...formDataValues];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads-${landingPageName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`${leadsToExport.length} leads exported!`);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const safeStr = (val: any): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };
  const getLeadName = (lead: FormLead) => safeStr(lead.name) || safeStr(lead.form_data?.name) || safeStr(lead.form_data?.nome) || '-';
  const getLeadEmail = (lead: FormLead) => safeStr(lead.email) || safeStr(lead.form_data?.email) || '-';
  const getLeadPhone = (lead: FormLead) => safeStr(lead.phone) || safeStr(lead.form_data?.phone) || safeStr(lead.form_data?.telefone) || '-';

  // Get custom field value for a lead
  const getCustomFieldValue = (lead: FormLead, field: string) => {
    const value = lead.form_data?.[field];
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Format field name for display (capitalize, replace underscores)
  const formatFieldName = (field: string) => {
    return field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Leads Capturados
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {landingPageName} • {totalLeads} leads
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadLeads}
              disabled={isLoading}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Atualizar"
            >
              <RefreshCw className={`w-5 h-5 text-gray-500 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={exportToCSV}
              disabled={filteredLeads.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors text-sm"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {selectedLeads.size > 0 ? `Exportar (${selectedLeads.size})` : 'Exportar CSV'}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nome, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white"
              />
              <span className="text-gray-400 text-sm">até</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Users className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Nenhum lead encontrado
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                {searchTerm || startDate || endDate
                  ? 'Tente ajustar os filtros'
                  : 'Os leads capturados aparecerão aqui'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto scrollbar-hide">
              <table className="w-full min-w-max">
                <thead className="bg-gray-50 dark:bg-gray-900/50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        checked={selectedLeads.size === filteredLeads.length && filteredLeads.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Nome</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">E-mail</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Telefone</th>
                    {/* Data/hora logo após Telefone: no fim da tabela ela ficava
                        escondida atrás das colunas dinâmicas (pedido Nexus 18/08) */}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Data de entrada</th>
                    {/* Dynamic custom field columns */}
                    {customFields.map((field) => (
                      <th
                        key={field}
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                      >
                        {formatFieldName(field)}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Origem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredLeads.map((lead) => (
                    <tr
                      key={lead.id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                        selectedLeads.has(lead.id) ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedLeads.has(lead.id)}
                          onChange={() => handleSelectLead(lead.id)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {getLeadName(lead)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {getLeadEmail(lead)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {getLeadPhone(lead)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {formatDate(lead.created_at || lead.createdAt || '')}
                        </span>
                      </td>
                      {/* Dynamic custom field values */}
                      {customFields.map((field) => (
                        <td key={field} className="px-4 py-3">
                          <span className="text-sm text-gray-600 dark:text-gray-300 max-w-[200px] truncate block" title={getCustomFieldValue(lead, field)}>
                            {getCustomFieldValue(lead, field)}
                          </span>
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        {lead.utm_source ? (
                          <span className="text-xs px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full">
                            {lead.utm_source}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Direto</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <p className="text-sm text-gray-500">
              Página {currentPage} de {totalPages} • {totalLeads} leads
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadsModal;
